import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import {
  Box,
  Button,
  Grid,
  TextField,
  Typography,
  Paper,
  Stack,
  Autocomplete,
  Divider,
  Alert,
  AlertTitle,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Chip,
} from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import ClearIcon from "@mui/icons-material/Clear";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import DeleteIcon from "@mui/icons-material/Delete";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import PendingActionsIcon from "@mui/icons-material/PendingActions";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import LocalFloristIcon from "@mui/icons-material/LocalFlorist";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { useUser } from "../../Context/UserContext";
import DynamicTable from "../Dynamic/DynamicTable";
import DynamicOffcanvas from "../Dynamic/DynamicOffcanvas";
import { toast } from "react-toastify";

const API = "/api";
const canModify = (role) => role === "block_admin";

const now = new Date();
const baseYear =
  now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
const currentFY = `${baseYear}-${baseYear + 1}`;
const years = Array.from(
  { length: 10 },
  (_, i) => `${baseYear + i}-${baseYear + i + 1}`,
);

const NurseryTarget = () => {
  const { token, role, user, authData, userId } = useUser();

  // Data States
  const [data, setData] = useState([]);
  const [nurseries, setNurseries] = useState([]);
  const [schemes, setSchemes] = useState([]);
  const [blockTargetLimits, setBlockTargetLimits] = useState([]);
  const [masterDepartments, setMasterDepartments] = useState([]);
  const [masterDistricts, setMasterDistricts] = useState([]);
  const [masterBlocks, setMasterBlocks] = useState([]);

  // UI States
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [userDistrictName, setUserDistrictName] = useState("Loading...");
  const [userBlockName, setUserBlockName] = useState("Loading...");
  const [overallBalance, setOverallBalance] = useState(0);

  // Detailed pending items
  const [pendingDetails, setPendingDetails] = useState([]);

  const [deleteModal, setDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [existModal, setExistModal] = useState(false);
  const [existInfo, setExistInfo] = useState(null);

  // Filters
  const [filterDepartment, setFilterDepartment] = useState(null);
  const [filterDistrict, setFilterDistrict] = useState(null);
  const [filterBlock, setFilterBlock] = useState(null);
  const [filterNursery, setFilterNursery] = useState(null);
  const [filterScheme, setFilterScheme] = useState(null);

  const [form, setForm] = useState({
    department_id: null,
    nursery_id: null,
    financial_year: currentFY,
    scheme_type: null,
    scheme_id: null,
    target_quantity: "",
  });
  const greenInputSx = {
    "& .MuiOutlinedInput-root": {
      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: "#4caf50",
        borderWidth: 1.5,
      },
      "&:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: "#2e7d32 !important",
        borderWidth: 2,
      },
      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: "#1b5e20",
        borderWidth: 2,
      },
    },
    "& .MuiInputLabel-root": {
      color: "#000000",
      fontWeight: 500,
    },
    "& .MuiInputLabel-root.Mui-focused": {
      color: "#000000",
    },
  };
  const [errors, setErrors] = useState({});
  const hasModifyPermission = canModify(role);
  const config = { headers: { Authorization: `Bearer ${token}` } };

  // ✅ FILTER VISIBILITY LOGIC
  const showDeptFilter = role === "superadmin";
  const showDistFilter = role === "superadmin" || role === "department_admin";
  const showBlockFilter =
    role === "superadmin" ||
    role === "department_admin" ||
    role === "district_admin";
  const showNurseryFilter =
    role === "superadmin" ||
    role === "department_admin" ||
    role === "district_admin" ||
    role === "block_admin";
  const showSchemeFilter = true;

  // ================= 1. FETCH DATA =================
  const fetchTargets = async () => {
    try {
      const res = await axios.get(
        `${API}/targetproductioncenter?user_id=${userId}`,
        config,
      );
      if (res.data) {
        const freshData = res.data.data || [];
        setData(freshData);
        return freshData;
      }
      setData([]);
      return [];
    } catch (err) {
      console.error("Fetch Targets Error:", err);
      setData([]);
      return [];
    }
  };

  const fetchInitialData = async () => {
    try {
      const [
        nurseryRes,
        schemeRes,
        blockLimitsRes,
        deptRes,
        distRes,
        blockRes,
      ] = await Promise.all([
        axios.get(`${API}/targetproductioncenter/productioncenters`, {
          ...config,
          params: { block_id: authData?.block_id },
        }),
        axios.get(`${API}/targetdepartment/schemes/all`, config),
        axios.get(`${API}/targetblock?user_id=${userId}`, config),
        axios.get(`${API}/officers/departments`, config),
        axios.get(`${API}/master/districts`, config),
        axios.get(`${API}/master/blocks`, config),
      ]);

      const rawNurseries = nurseryRes.data?.data || nurseryRes.data || [];
      // ✅ SORT NURSERIES ALPHABETICALLY
      const sortedNurseries = rawNurseries.sort((a, b) =>
        (a.name || a.productioncenter_name || "").localeCompare(
          b.name || b.productioncenter_name || "",
        ),
      );
      setNurseries(
        sortedNurseries.map((n) => ({
          id: n.id || n.productioncenter_id,
          name: n.productioncenter_name || n.name,
        })),
      );

      setSchemes(schemeRes.data || []);
      setMasterDepartments(deptRes.data?.data || deptRes.data || []);
      setMasterDistricts(distRes.data?.data || distRes.data || []);
      setMasterBlocks(blockRes.data?.data || blockRes.data || []);

      setUserDistrictName(authData?.district_name || "My District");
      setUserBlockName(authData?.block_name || "My Block");

      const limits = blockLimitsRes.data?.data || blockLimitsRes.data || [];
      setBlockTargetLimits(limits);

      calculateOverallBalance(limits, [], form.financial_year);
    } catch (err) {
      console.error("Setup Error:", err);
    }
  };

  // ================= 2. OVERALL BALANCE LOGIC =================
  const calculateOverallBalance = (
    currentLimits = blockTargetLimits,
    currentData = data,
    selectedYearStr = currentFY,
  ) => {
    let totalBalance = 0;
    const details = [];
    const selectedYear = Number(selectedYearStr.split("-")[0]);

    currentLimits.forEach((lim) => {
      if (!lim.start_date) return;
      const limYear = new Date(lim.start_date).getFullYear();
      if (limYear !== selectedYear) return;

      const dbUsed = currentData.reduce((sum, row) => {
        if (!row.start_date) return sum;
        const rowYear = new Date(row.start_date).getFullYear();
        if (rowYear !== selectedYear) return sum;
        if (
          Number(row.target_department_id) !== Number(lim.target_department_id)
        )
          return sum;

        if (lim.scheme_type === "Scheme") {
          if (Number(row.scheme_id) === Number(lim.scheme_id))
            return sum + Number(row.target_quantity || 0);
        } else {
          if (row.scheme_type === "Non-Scheme")
            return sum + Number(row.target_quantity || 0);
        }
        return sum;
      }, 0);

      const balance = Number(lim.target_quantity) - dbUsed;

      if (balance !== 0) {
        totalBalance += balance;
        details.push({
          id: lim.id || lim.target_id,
          department_name: lim.department_name || "Department",
          scheme_name:
            lim.scheme_type === "Scheme" ? lim.scheme_name : "Non-Scheme",
          balance: balance,
          isOverAssigned: balance < 0,
        });
      }
    });

    setOverallBalance(totalBalance);
    setPendingDetails(details);
  };

  useEffect(() => {
    if (token && userId) {
      fetchTargets().then((freshData) => {
        if (blockTargetLimits.length > 0) {
          calculateOverallBalance(
            blockTargetLimits,
            freshData,
            form.financial_year,
          );
        }
      });
      fetchInitialData();
    }
  }, [token, userId]);

  useEffect(() => {
    calculateOverallBalance(blockTargetLimits, data, form.financial_year);
  }, [data, blockTargetLimits, form.financial_year]);

  // ================= 3. BLOCK RESTRICTION LOGIC =================
  const availableDepartments = useMemo(() => {
    const uniqueDepts = {};
    (blockTargetLimits || []).forEach((lim) => {
      if (lim.target_department_id && lim.department_name) {
        uniqueDepts[lim.target_department_id] = lim.department_name;
      }
    });
    return Object.entries(uniqueDepts).map(([id, name]) => ({
      id: Number(id),
      name,
    }));
  }, [blockTargetLimits]);

  const displayedBlockLimits = useMemo(() => {
    if (!form.financial_year || !form.department_id) return [];
    return blockTargetLimits.filter((lim) => {
      const limYear = lim.start_date
        ? `${new Date(lim.start_date).getFullYear()}-${new Date(lim.start_date).getFullYear() + 1}`
        : null;
      return (
        limYear === form.financial_year &&
        Number(lim.target_department_id) === Number(form.department_id)
      );
    });
  }, [blockTargetLimits, form.financial_year, form.department_id]);

  const blockAllowedTypes = useMemo(() => {
    const types = new Set(displayedBlockLimits.map((l) => l.scheme_type));
    if (types.has("Non-Scheme") && types.has("Scheme"))
      return ["Non-Scheme", "Scheme"];
    if (types.has("Non-Scheme")) return ["Non-Scheme"];
    if (types.has("Scheme")) return ["Scheme"];
    return [];
  }, [displayedBlockLimits]);

  const blockAllowedSchemeIds = useMemo(() => {
    return displayedBlockLimits
      .filter((l) => l.scheme_type === "Scheme")
      .map((l) => Number(l.scheme_id));
  }, [displayedBlockLimits]);

  // ================= 4. NURSERY HIERARCHY LOGIC =================
  const getExistingForNurseryYear = (
    targetData,
    nurseryId,
    fy,
    excludeEditId = null,
  ) => {
    return targetData.filter((item) => {
      const isEditItem =
        excludeEditId !== null && String(item.id) === String(excludeEditId);
      const itemYear = item.start_date
        ? `${new Date(item.start_date).getFullYear()}-${new Date(item.start_date).getFullYear() + 1}`
        : null;
      return (
        Number(item.productioncenter_id) === Number(nurseryId) &&
        itemYear === fy &&
        !isEditItem
      );
    });
  };

  const isNurseryComplete = (nurseryId, fy, excludeEditId = null) => {
    if (blockAllowedTypes.length === 0) return true;
    const existing = getExistingForNurseryYear(
      data,
      nurseryId,
      fy,
      excludeEditId,
    );
    const hasNonScheme = existing.some((t) => t.scheme_type === "Non-Scheme");
    const usedSchemeIds = existing
      .filter((t) => t.scheme_type === "Scheme" && t.scheme_id)
      .map((t) => Number(t.scheme_id));
    const nonSchemeDone = blockAllowedTypes.includes("Non-Scheme")
      ? hasNonScheme
      : true;
    const schemesDone = blockAllowedTypes.includes("Scheme")
      ? blockAllowedSchemeIds.every((s) => usedSchemeIds.includes(s))
      : true;
    return nonSchemeDone && schemesDone;
  };

  const availableNurseries = useMemo(() => {
    if (!form.financial_year || !form.department_id) return [];
    return nurseries.filter(
      (nursery) => !isNurseryComplete(nursery.id, form.financial_year, editId),
    );
  }, [
    data,
    form.financial_year,
    form.department_id,
    nurseries,
    blockAllowedTypes,
    blockAllowedSchemeIds,
    editId,
  ]);

  const availableTypes = useMemo(() => {
    if (
      blockAllowedTypes.length === 0 ||
      !form.nursery_id ||
      !form.financial_year
    )
      return [];
    const existing = getExistingForNurseryYear(
      data,
      form.nursery_id,
      form.financial_year,
      editId,
    );
    const hasNonScheme = existing.some((t) => t.scheme_type === "Non-Scheme");
    const usedSchemeIds = existing
      .filter((t) => t.scheme_type === "Scheme" && t.scheme_id)
      .map((t) => Number(t.scheme_id));
    const hasRemainingSchemes = blockAllowedSchemeIds.some(
      (s) => !usedSchemeIds.includes(s),
    );
    const finalTypes = [];
    if (blockAllowedTypes.includes("Non-Scheme") && !hasNonScheme)
      finalTypes.push("Non-Scheme");
    if (blockAllowedTypes.includes("Scheme") && hasRemainingSchemes)
      finalTypes.push("Scheme");
    return finalTypes;
  }, [
    data,
    form.nursery_id,
    form.financial_year,
    editId,
    blockAllowedTypes,
    blockAllowedSchemeIds,
  ]);

  const availableSchemes = useMemo(() => {
    if (
      form.scheme_type !== "Scheme" ||
      !form.nursery_id ||
      !form.financial_year
    )
      return [];
    const existing = getExistingForNurseryYear(
      data,
      form.nursery_id,
      form.financial_year,
      editId,
    );
    const usedSchemeIds = existing
      .filter((t) => t.scheme_type === "Scheme" && t.scheme_id)
      .map((t) => Number(t.scheme_id));
    return schemes.filter(
      (s) =>
        blockAllowedSchemeIds.includes(Number(s.id)) &&
        !usedSchemeIds.includes(Number(s.id)),
    );
  }, [
    data,
    form.nursery_id,
    form.financial_year,
    editId,
    form.scheme_type,
    schemes,
    blockAllowedSchemeIds,
  ]);

  const hierarchyStatus = useMemo(() => {
    if (!form.nursery_id || !form.financial_year) return null;
    const existing = getExistingForNurseryYear(
      data,
      form.nursery_id,
      form.financial_year,
      null,
    );
    const hasNonScheme = existing.some((t) => t.scheme_type === "Non-Scheme");
    const usedSchemeIds = existing
      .filter((t) => t.scheme_type === "Scheme" && t.scheme_id)
      .map((t) => Number(t.scheme_id));
    return {
      nonSchemeSet: hasNonScheme,
      schemesSet: usedSchemeIds.length,
      totalSchemes: blockAllowedSchemeIds.length,
      remainingSchemes: blockAllowedSchemeIds.length - usedSchemeIds.length,
    };
  }, [form.nursery_id, form.financial_year, data, blockAllowedSchemeIds]);

  // Auto-correct form
  useEffect(() => {
    if (form.scheme_type && !availableTypes.includes(form.scheme_type))
      setForm((prev) => ({ ...prev, scheme_type: null, scheme_id: null }));
  }, [availableTypes, form.scheme_type]);

  useEffect(() => {
    if (form.scheme_type === "Scheme" && form.scheme_id) {
      if (
        !availableSchemes.some((s) => Number(s.id) === Number(form.scheme_id))
      )
        setForm((prev) => ({ ...prev, scheme_id: null }));
    }
  }, [availableSchemes, form.scheme_type, form.scheme_id]);

  useEffect(() => {
    if (
      form.nursery_id &&
      !availableNurseries.some((n) => n.id === form.nursery_id)
    )
      setForm((prev) => ({
        ...prev,
        nursery_id: null,
        scheme_type: null,
        scheme_id: null,
      }));
  }, [availableNurseries, form.nursery_id]);

  useEffect(() => {
    if (
      form.department_id &&
      !availableDepartments.some((d) => d.id === form.department_id)
    )
      setForm((prev) => ({
        ...prev,
        department_id: null,
        nursery_id: null,
        scheme_type: null,
        scheme_id: null,
      }));
  }, [availableDepartments, form.department_id]);

  // ================= 5. LIMIT CALCULATION =================
  const getMaxAllowed = () => {
    if (!form.financial_year || !form.scheme_type || !form.department_id)
      return null;
    const isScheme = form.scheme_type === "Scheme";
    if (isScheme && !form.scheme_id) return null;

    let totalAllowed = 0;
    if (isScheme) {
      const masterLimit = blockTargetLimits.find(
        (lim) =>
          Number(lim.scheme_id) === Number(form.scheme_id) &&
          Number(lim.target_department_id) === Number(form.department_id) &&
          lim.start_date &&
          `${new Date(lim.start_date).getFullYear()}-${new Date(lim.start_date).getFullYear() + 1}` ===
            form.financial_year,
      );
      totalAllowed = Number(masterLimit?.target_quantity || 0);
    } else {
      const masterLimit = blockTargetLimits.find(
        (lim) =>
          lim.scheme_type === "Non-Scheme" &&
          Number(lim.target_department_id) === Number(form.department_id) &&
          lim.start_date &&
          `${new Date(lim.start_date).getFullYear()}-${new Date(lim.start_date).getFullYear() + 1}` ===
            form.financial_year,
      );
      totalAllowed = Number(masterLimit?.target_quantity || 0);
    }

    const dbUsed = data.reduce((sum, row) => {
      if (!row.start_date) return sum;
      const rowYear = `${new Date(row.start_date).getFullYear()}-${new Date(row.start_date).getFullYear() + 1}`;
      if (rowYear !== form.financial_year) return sum;
      if (editId !== null && String(row.id) === String(editId)) return sum;
      if (Number(row.target_department_id) !== Number(form.department_id))
        return sum;

      if (isScheme) {
        if (Number(row.scheme_id) === Number(form.scheme_id))
          return sum + Number(row.target_quantity || 0);
      } else {
        if (row.scheme_type === "Non-Scheme")
          return sum + Number(row.target_quantity || 0);
      }
      return sum;
    }, 0);

    return totalAllowed - dbUsed;
  };

  const getBlockRemainingBalance = (lim) => {
    const limYear = lim.start_date
      ? `${new Date(lim.start_date).getFullYear()}-${new Date(lim.start_date).getFullYear() + 1}`
      : null;
    if (limYear !== form.financial_year) return Number(lim.target_quantity);
    const dbUsed = data.reduce((sum, row) => {
      if (!row.start_date) return sum;
      const rowYear = `${new Date(row.start_date).getFullYear()}-${new Date(row.start_date).getFullYear() + 1}`;
      if (rowYear !== form.financial_year) return sum;
      if (editId !== null && String(row.id) === String(editId)) return sum;
      if (Number(row.target_department_id) !== Number(lim.target_department_id))
        return sum;
      if (lim.scheme_type === "Scheme") {
        if (Number(row.scheme_id) === Number(lim.scheme_id))
          return sum + Number(row.target_quantity || 0);
      } else {
        if (row.scheme_type === "Non-Scheme")
          return sum + Number(row.target_quantity || 0);
      }
      return sum;
    }, 0);
    return Number(lim.target_quantity) - dbUsed;
  };

  // ================= 6. FILTER LOGIC & SORTING =================
  const filteredData = useMemo(() => {
    if (!Array.isArray(data)) return [];
    let result = data.filter((row) => {
      const matchDept =
        !filterDepartment ||
        Number(row.target_department_id) === Number(filterDepartment.id);
      const matchDist =
        !filterDistrict ||
        Number(row.district_id) === Number(filterDistrict.id);
      const matchBlock =
        !filterBlock || Number(row.block_id) === Number(filterBlock.id);
      const matchNursery =
        !filterNursery ||
        Number(row.productioncenter_id) === Number(filterNursery.id);
      let matchScheme = true;
      if (filterScheme) {
        if (filterScheme === "Non-Scheme")
          matchScheme = row.scheme_type === "Non-Scheme";
        else
          matchScheme =
            row.scheme_type === "Scheme" &&
            String(row.scheme_id) === String(filterScheme.id);
      }
      return (
        matchDept && matchDist && matchBlock && matchNursery && matchScheme
      );
    });

    // ✅ SORT TABLE DATA BY NURSERY NAME (ASCENDING)
    return result.sort((a, b) => {
      const nameA = (a.productioncenter_name || "").toString().toLowerCase();
      const nameB = (b.productioncenter_name || "").toString().toLowerCase();
      return nameA.localeCompare(nameB);
    });
  }, [
    data,
    filterDepartment,
    filterDistrict,
    filterBlock,
    filterNursery,
    filterScheme,
  ]);

  // ✅ CALCULATE TOTAL TARGET VALUE
  const totalTargetValue = useMemo(() => {
    return filteredData.reduce(
      (sum, row) => sum + Number(row.target_quantity || 0),
      0,
    );
  }, [filteredData]);

  // ================= 7. FORM HELPERS & VALIDATION =================
  const handleChange = (name, value) => {
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name])
      setErrors((prev) => {
        const n = { ...prev };
        delete n[name];
        return n;
      });
  };
  const resetHierarchy = () => {
    setForm((prev) => ({
      ...prev,
      nursery_id: null,
      scheme_type: null,
      scheme_id: null,
    }));
    setErrors((prev) => {
      const n = { ...prev };
      delete n.nursery_id;
      delete n.scheme_type;
      delete n.scheme_id;
      return n;
    });
  };
  const resetTypeAndScheme = () => {
    setForm((prev) => ({ ...prev, scheme_type: null, scheme_id: null }));
  };

  const validate = () => {
    const tempErrors = {};
    if (!form.department_id) tempErrors.department_id = "Select department";
    if (!form.nursery_id) tempErrors.nursery_id = "Select nursery";
    if (!form.financial_year) tempErrors.financial_year = "Select FY";
    if (!form.scheme_type) tempErrors.scheme_type = "Select type";
    if (form.scheme_type === "Scheme" && !form.scheme_id)
      tempErrors.scheme_id = "Select scheme";
    if (!form.target_quantity || Number(form.target_quantity) <= 0)
      tempErrors.target_quantity = "Enter valid qty";

    const max = getMaxAllowed();
    if (
      !tempErrors.target_quantity &&
      max !== null &&
      max < Number(form.target_quantity)
    ) {
      tempErrors.target_quantity =
        max < 0
          ? `Block limit exceeded by ${Math.abs(max).toLocaleString()}. Delete nursery targets first.`
          : `Exceeds limit (${max.toLocaleString()})`;
    }
    setErrors(tempErrors);
    return Object.keys(tempErrors).length === 0;
  };

  // ================= 8. CRUD ACTIONS =================
  const handleSubmit = async () => {
    if (!validate()) return;

    const duplicate = data.find((item) => {
      if (editId && String(item.id) === String(editId)) return false;
      if (Number(item.productioncenter_id) !== Number(form.nursery_id))
        return false;
      if (Number(item.target_department_id) !== Number(form.department_id))
        return false;
      const itemYear = item.start_date
        ? `${new Date(item.start_date).getFullYear()}-${new Date(item.start_date).getFullYear() + 1}`
        : null;
      if (itemYear !== form.financial_year) return false;
      if (form.scheme_type === "Scheme")
        return (
          item.scheme_type === "Scheme" &&
          Number(item.scheme_id) === Number(form.scheme_id)
        );
      return item.scheme_type === "Non-Scheme";
    });

    if (duplicate) {
      const nName =
        nurseries.find((n) => n.id === form.nursery_id)?.name || "Nursery";
      const tName =
        form.scheme_type === "Scheme"
          ? schemes.find((s) => s.id === form.scheme_id)?.name
          : "Non-Scheme";
      setExistInfo({ name: nName, type: tName });
      setExistModal(true);
      return;
    }

    try {
      if (editId)
        await axios.delete(`${API}/targetproductioncenter/${editId}`, config);
      const fyStart = form.financial_year.split("-")[0];
      const payload = {
        district_id: authData?.district_id,
        block_id: authData?.block_id,
        productioncenter_id: Number(form.nursery_id),
        target_department_id: Number(form.department_id),
        target_quantity: Number(form.target_quantity),
        start_date: `${fyStart}-04-01`,
        end_date: `${Number(fyStart) + 1}-03-31`,
        created_by: user?.id || 1,
        scheme_type: form.scheme_type,
        scheme_id:
          form.scheme_type === "Scheme" ? Number(form.scheme_id) : null,
      };

      await axios.post(`${API}/targetproductioncenter`, payload, config);
      const freshData = await fetchTargets();

      const isComplete = isNurseryComplete(
        form.nursery_id,
        form.financial_year,
      );
      if (!isComplete) {
        const existing = getExistingForNurseryYear(
          freshData,
          form.nursery_id,
          form.financial_year,
        );
        const hasNonScheme = existing.some(
          (t) => t.scheme_type === "Non-Scheme",
        );
        const usedSchemeIds = existing
          .filter((t) => t.scheme_type === "Scheme" && t.scheme_id)
          .map((t) => Number(t.scheme_id));
        const hasRemainingSchemes = blockAllowedSchemeIds.some(
          (s) => !usedSchemeIds.includes(s),
        );
        const nextType =
          blockAllowedTypes.includes("Non-Scheme") && !hasNonScheme
            ? "Non-Scheme"
            : blockAllowedTypes.includes("Scheme") && hasRemainingSchemes
              ? "Scheme"
              : null;
        setForm((prev) => ({
          ...prev,
          scheme_type: nextType,
          scheme_id: null,
          target_quantity: "",
        }));
        setErrors({});
        toast.success("Saved! More targets pending.");
      } else {
        handleClose();
        toast.success("All targets set for this nursery!");
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to save");
    }
  };

  const handleEdit = (row) => {
    setEditId(row.id || row.target_id);
    const itemYear = row.start_date
      ? `${new Date(row.start_date).getFullYear()}-${new Date(row.start_date).getFullYear() + 1}`
      : currentFY;
    setForm({
      department_id: row.target_department_id,
      nursery_id: row.productioncenter_id,
      financial_year: itemYear,
      scheme_type: row.scheme_type || (row.scheme_id ? "Scheme" : "Non-Scheme"),
      scheme_id: row.scheme_id || null,
      target_quantity: row.target_quantity,
    });
    setErrors({});
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditId(null);
    setForm({
      department_id: null,
      nursery_id: null,
      financial_year: currentFY,
      scheme_type: null,
      scheme_id: null,
      target_quantity: "",
    });
    setErrors({});
  };
  const confirmDelete = async () => {
    try {
      await axios.delete(
        `${API}/targetproductioncenter/${deleteTarget.id || deleteTarget.target_id}`,
        config,
      );
      toast.success("Deleted");
      fetchTargets();
    } catch {
      toast.error("Failed");
    } finally {
      setDeleteModal(false);
    }
  };

  const columns = [
    {
      field: "department_name",
      headerName: "Department",
      render: (r) => r.department_name || "—",
    },
    { field: "district_name", headerName: "District" },
    { field: "block_name", headerName: "Block" },
    { field: "productioncenter_name", headerName: "Nursery" },
    { field: "scheme_type", headerName: "Type" },
    {
      field: "scheme_name",
      headerName: "Scheme",
      render: (r) => r.scheme_name || "—",
    },
    { field: "target_quantity", headerName: "Target" },
    {
      field: "start_date",
      headerName: "FY",
      render: (r) =>
        r.start_date
          ? `${new Date(r.start_date).getFullYear()}-${new Date(r.start_date).getFullYear() + 1}`
          : "—",
    },
  ];

  // ================= 9. RENDER =================
  return (
    <Box p={3}>
      <Paper
        elevation={0}
        sx={{ p: 2, mb: 1, border: "1px solid #eee", borderRadius: 2 }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          spacing={2}
          flexWrap="wrap"
          gap={2}
        >
          <Stack
            direction="row"
            spacing={2}
            flex={1}
            alignItems="center"
            flexWrap="wrap"
            gap={1}
          >
            <FilterListIcon sx={{ color: "#2e7d32" }} />

            {showDeptFilter && (
              <Autocomplete
                options={masterDepartments}
                getOptionLabel={(o) => o.name}
                value={filterDepartment}
                onChange={(e, v) => setFilterDepartment(v)}
                isOptionEqualToValue={(o, v) => o.id === v?.id}
                size="small"
                sx={{ minWidth: 200 }}
                clearIcon={<ClearIcon fontSize="small" />}
                renderInput={(p) => (
                  <TextField
                    {...p}
                    label="Department"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#4caf50",
                          borderWidth: 1.5,
                        },
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#2e7d32 !important",
                          borderWidth: 2,
                        },
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#1b5e20",
                          borderWidth: 2,
                        },
                      },
                      "& .MuiInputLabel-root": {
                        color: "#000000",
                        fontWeight: 500,
                      },
                      "& .MuiInputLabel-root.Mui-focused": { color: "#000000" },
                    }}
                  />
                )}
              />
            )}

            {showDistFilter && (
              <Autocomplete
                options={masterDistricts}
                getOptionLabel={(o) => o.District_Name}
                value={filterDistrict}
                onChange={(e, v) => setFilterDistrict(v)}
                isOptionEqualToValue={(o, v) => o.id === v?.id}
                size="small"
                sx={{ minWidth: 200 }}
                clearIcon={<ClearIcon fontSize="small" />}
                renderInput={(p) => (
                  <TextField
                    {...p}
                    label="District"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#4caf50",
                          borderWidth: 1.5,
                        },
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#2e7d32 !important",
                          borderWidth: 2,
                        },
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#1b5e20",
                          borderWidth: 2,
                        },
                      },
                      "& .MuiInputLabel-root": {
                        color: "#000000",
                        fontWeight: 500,
                      },
                      "& .MuiInputLabel-root.Mui-focused": { color: "#000000" },
                    }}
                  />
                )}
              />
            )}

            {showBlockFilter && (
              <Autocomplete
                options={masterBlocks}
                getOptionLabel={(o) => o.Block_Name}
                value={filterBlock}
                onChange={(e, v) => setFilterBlock(v)}
                isOptionEqualToValue={(o, v) => o.id === v?.id}
                size="small"
                sx={{ minWidth: 200 }}
                clearIcon={<ClearIcon fontSize="small" />}
                renderInput={(p) => (
                  <TextField
                    {...p}
                    label="Block"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#4caf50",
                          borderWidth: 1.5,
                        },
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#2e7d32 !important",
                          borderWidth: 2,
                        },
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#1b5e20",
                          borderWidth: 2,
                        },
                      },
                      "& .MuiInputLabel-root": {
                        color: "#000000",
                        fontWeight: 500,
                      },
                      "& .MuiInputLabel-root.Mui-focused": { color: "#000000" },
                    }}
                  />
                )}
              />
            )}

            {showNurseryFilter && (
              <Autocomplete
                options={nurseries}
                getOptionLabel={(o) => o.name}
                value={filterNursery}
                onChange={(e, v) => setFilterNursery(v)}
                isOptionEqualToValue={(o, v) => o.id === v?.id}
                size="small"
                sx={{ minWidth: 200 }}
                clearIcon={<ClearIcon fontSize="small" />}
                renderInput={(p) => (
                  <TextField
                    {...p}
                    label="Nursery"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#4caf50",
                          borderWidth: 1.5,
                        },
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#2e7d32 !important",
                          borderWidth: 2,
                        },
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#1b5e20",
                          borderWidth: 2,
                        },
                      },
                      "& .MuiInputLabel-root": {
                        color: "#000000",
                        fontWeight: 500,
                      },
                      "& .MuiInputLabel-root.Mui-focused": { color: "#000000" },
                    }}
                  />
                )}
              />
            )}

            {showSchemeFilter && (
              <Autocomplete
                options={[{ id: "Non-Scheme", name: "Non-Scheme" }, ...schemes]}
                getOptionLabel={(o) => o.name}
                value={filterScheme}
                onChange={(e, v) => setFilterScheme(v)}
                isOptionEqualToValue={(o, v) => String(o.id) === String(v?.id)}
                size="small"
                sx={{ minWidth: 250 }}
                clearIcon={<ClearIcon fontSize="small" />}
                renderInput={(p) => (
                  <TextField
                    {...p}
                    label="Scheme / Type"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#4caf50",
                          borderWidth: 1.5,
                        },
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#2e7d32 !important",
                          borderWidth: 2,
                        },
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#1b5e20",
                          borderWidth: 2,
                        },
                      },
                      "& .MuiInputLabel-root": {
                        color: "#000000",
                        fontWeight: 500,
                      },
                      "& .MuiInputLabel-root.Mui-focused": { color: "#000000" },
                    }}
                  />
                )}
              />
            )}
          </Stack>

          {/* ✅ UPDATED: Conditional Rendering for Target Button / Status Chip */}
          <Stack direction="row" spacing={2} alignItems="center">
            {hasModifyPermission && overallBalance !== 0 && (
              <>
                <Chip
                  icon={<PendingActionsIcon style={{ fontSize: 18 }} />}
                  label={`${overallBalance.toLocaleString()} ${overallBalance < 0 ? "Over-Assigned" : "Pending"}`}
                  color={overallBalance < 0 ? "error" : "warning"}
                  variant="outlined"
                  sx={{
                    fontWeight: 600,
                    borderColor: overallBalance < 0 ? "red" : "orange",
                    color: overallBalance < 0 ? "#b71c1c" : "#e65100",
                  }}
                />
                {overallBalance > 0 && (
                  <Button
                    variant="contained"
                    color="success"
                    onClick={() => setOpen(true)}
                    sx={{ fontWeight: 600 }}
                  >
                    Set Target
                  </Button>
                )}
              </>
            )}

            {hasModifyPermission && overallBalance === 0 && (
              <Chip
                icon={<TaskAltIcon style={{ fontSize: 18 }} />}
                label="All Targets Completed"
                color="success"
                sx={{
                  fontWeight: 600,
                  bgcolor: "#e8f5e9",
                  color: "#2e7d32",
                  border: "1px solid #a5d6a7",
                }}
              />
            )}
          </Stack>
        </Stack>
      </Paper>

      {/* ✅ UPDATED: Breakdown now shows RED if negative remainder exists */}
      {hasModifyPermission && pendingDetails.length > 0 && (
        <Paper
          elevation={0}
          sx={{
            p: 2,
            mb: 3,
            borderRadius: 2,
            border: pendingDetails.some((d) => d.isOverAssigned)
              ? "1px dashed #ef9a9a"
              : "1px dashed #ffcc80",
            bgcolor: pendingDetails.some((d) => d.isOverAssigned)
              ? "#ffebee"
              : "#fffde7",
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center" mb={1}>
            {pendingDetails.some((d) => d.isOverAssigned) ? (
              <ErrorOutlineIcon sx={{ fontSize: 18, color: "#b71c1c" }} />
            ) : (
              <PendingActionsIcon sx={{ fontSize: 18, color: "#e65100" }} />
            )}
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 700,
                color: pendingDetails.some((d) => d.isOverAssigned)
                  ? "#b71c1c"
                  : "#e65100",
              }}
            >
              {pendingDetails.some((d) => d.isOverAssigned)
                ? "Over-Assigned Breakdown"
                : "Pending Breakdown"}{" "}
              ({form.financial_year})
            </Typography>
          </Stack>
          <Stack direction="row" spacing={1} flexWrap="wrap" gap={1} useFlexGap>
            {pendingDetails.map((det) => (
              <Chip
                key={det.id}
                icon={
                  det.isOverAssigned ? (
                    <WarningAmberIcon sx={{ fontSize: 16 }} />
                  ) : (
                    <LocalFloristIcon sx={{ fontSize: 16 }} />
                  )
                }
                label={`${det.department_name} - ${det.scheme_name}: ${det.balance.toLocaleString()}`}
                variant="outlined"
                size="small"
                sx={{
                  borderColor: det.isOverAssigned ? "#e57373" : "#ffb74d",
                  bgcolor: "#ffffff",
                  color: det.isOverAssigned ? "#b71c1c" : "#bf360c",
                  fontWeight: 500,
                  "& .MuiChip-icon": {
                    color: det.isOverAssigned ? "#d32f2f" : "#e65100",
                  },
                }}
              />
            ))}
          </Stack>
        </Paper>
      )}

      {/* ✅ TOTAL TARGET VALUE DISPLAY ABOVE TABLE */}
      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 2,
          border: "1px solid #c8e6c9",
          borderRadius: 2,
          bgcolor: "#f1f8e9",
        }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography variant="h6" sx={{ fontWeight: 600, color: "#2e7d32" }}>
            Total Target Value
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 700, color: "#1b5e20" }}>
            {totalTargetValue.toLocaleString()}
          </Typography>
        </Stack>
      </Paper>

      <DynamicTable
        data={filteredData}
        columns={columns}
        onEdit={hasModifyPermission ? handleEdit : undefined}
        onDelete={
          hasModifyPermission
            ? (r) => {
                setDeleteTarget(r);
                setDeleteModal(true);
              }
            : undefined
        }
      />

      <Dialog
        open={deleteModal}
        onClose={() => setDeleteModal(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          <Stack direction="row" alignItems="center" spacing={1}>
            <DeleteIcon color="error" />
            <Typography fontWeight={600}>Confirm Delete</Typography>
          </Stack>
        </DialogTitle>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteModal(false)}>Cancel</Button>
          <Button onClick={confirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={existModal}
        onClose={() => setExistModal(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          <Stack direction="row" alignItems="center" spacing={1}>
            <WarningAmberIcon color="warning" />
            <Typography fontWeight={600}>Target Exists</Typography>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Typography>
            Target for <strong>{existInfo?.name}</strong> with{" "}
            <strong>{existInfo?.type}</strong> is already set.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setExistModal(false)} variant="contained">
            OK
          </Button>
        </DialogActions>
      </Dialog>

      <DynamicOffcanvas
        open={open}
        title={editId ? "Edit Target" : "Set Nursery Target"}
        onClose={handleClose}
        actions={
          <Stack direction="row" spacing={2}>
            <Button
              variant="outlined"
              onClick={handleClose}
              sx={{
                borderColor: "#4caf50",
                color: "#4caf50",
                "&:hover": {
                  borderColor: "#2e7d32",
                  backgroundColor: "rgba(76, 175, 80, 0.08)",
                },
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              sx={{
                backgroundColor: "#2e7d32",
                "&:hover": { backgroundColor: "#1b5e20" },
              }}
            >
              {editId ? "Update" : "Save Target"}
            </Button>
          </Stack>
        }
      >
        {displayedBlockLimits.length > 0 && (
          <Alert
            severity={
              displayedBlockLimits.some((l) => getBlockRemainingBalance(l) < 0)
                ? "error"
                : "info"
            }
            variant="outlined"
            sx={{
              mb: 3,
              borderRadius: 2,
              "& .MuiAlert-message": { width: "100%" },
            }}
          >
            <AlertTitle sx={{ fontWeight: 600, fontSize: 14 }}>
              Block Targets ({form.financial_year})
            </AlertTitle>
            <Stack spacing={0.5} sx={{ mt: 0.5 }}>
              {displayedBlockLimits.map((lim) => {
                const bal = getBlockRemainingBalance(lim);
                return (
                  <Typography
                    key={lim.id}
                    variant="body2"
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      color: bal < 0 ? "error.main" : "text.primary",
                    }}
                  >
                    <span>
                      {lim.scheme_type === "Scheme"
                        ? lim.scheme_name
                        : "Non-Scheme"}
                      :
                    </span>
                    <strong>
                      {bal.toLocaleString()}{" "}
                      {bal < 0 ? "(Over-assigned)" : "left"}
                    </strong>
                  </Typography>
                );
              })}
              <Divider sx={{ my: 0.5 }} />
              <Typography
                variant="body2"
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  color:
                    displayedBlockLimits.reduce(
                      (acc, curr) => acc + getBlockRemainingBalance(curr),
                      0,
                    ) < 0
                      ? "error.main"
                      : "text.primary",
                }}
              >
                <span>Overall Remaining:</span>
                <strong>
                  {displayedBlockLimits
                    .reduce(
                      (acc, curr) => acc + getBlockRemainingBalance(curr),
                      0,
                    )
                    .toLocaleString()}
                </strong>
              </Typography>
            </Stack>
          </Alert>
        )}

        {hierarchyStatus && (
          <Alert
            severity="success"
            variant="outlined"
            sx={{
              mb: 3,
              borderRadius: 2,
              "& .MuiAlert-message": { width: "100%" },
            }}
          >
            <AlertTitle sx={{ fontWeight: 600, fontSize: 14 }}>
              Nursery Allocation Progress
            </AlertTitle>
            <Stack direction="row" spacing={3} sx={{ mt: 0.5 }}>
              {blockAllowedTypes.includes("Non-Scheme") && (
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  {hierarchyStatus.nonSchemeSet ? (
                    <CheckCircleOutlineIcon fontSize="small" color="success" />
                  ) : (
                    <RadioButtonUncheckedIcon
                      fontSize="small"
                      color="disabled"
                    />
                  )}
                  <Typography variant="body2">Non-Scheme</Typography>
                </Stack>
              )}
              {blockAllowedTypes.includes("Scheme") && (
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  {hierarchyStatus.remainingSchemes === 0 ? (
                    <CheckCircleOutlineIcon fontSize="small" color="success" />
                  ) : (
                    <RadioButtonUncheckedIcon
                      fontSize="small"
                      color="disabled"
                    />
                  )}
                  <Typography variant="body2">
                    Schemes ({hierarchyStatus.schemesSet}/
                    {hierarchyStatus.totalSchemes})
                  </Typography>
                </Stack>
              )}
            </Stack>
          </Alert>
        )}

        <Grid container spacing={2.5}>
          <Grid item xs={12} width="250px">
            <TextField
              fullWidth
              size="small"
              label="District"
              value={userDistrictName}
              InputProps={{ readOnly: true }}
              sx={greenInputSx}
            />
          </Grid>

          <Grid item xs={12} width="250px">
            <TextField
              fullWidth
              size="small"
              label="Block"
              value={userBlockName}
              InputProps={{ readOnly: true }}
              sx={greenInputSx}
            />
          </Grid>

          <Grid item xs={12} width="250px">
            <Autocomplete
              options={availableDepartments}
              getOptionLabel={(o) => o.name}
              value={
                availableDepartments.find((d) => d.id === form.department_id) ||
                null
              }
              onChange={(e, v) => {
                handleChange("department_id", v?.id || null);
                resetHierarchy();
              }}
              isOptionEqualToValue={(o, v) => o.id === v?.id}
              size="small"
              renderInput={(p) => (
                <TextField
                  {...p}
                  label="Department"
                  error={!!errors.department_id}
                  helperText={
                    errors.department_id ||
                    `${availableDepartments.length} dept(s) available`
                  }
                  sx={greenInputSx}
                />
              )}
            />
          </Grid>

          <Grid item xs={12} width="250px">
            <Autocomplete
              options={years}
              getOptionLabel={(o) => o}
              value={form.financial_year || null}
              onChange={(e, v) => {
                handleChange("financial_year", v || null);
                resetHierarchy();
              }}
              isOptionEqualToValue={(o, v) => o === v}
              size="small"
              renderInput={(p) => (
                <TextField
                  {...p}
                  label="Financial Year"
                  error={!!errors.financial_year}
                  helperText={errors.financial_year}
                  sx={greenInputSx}
                />
              )}
            />
          </Grid>

          <Grid item xs={12}>
            <Divider />
          </Grid>

          <Grid item xs={12} width="250px">
            <Autocomplete
              options={availableNurseries}
              getOptionLabel={(o) => o.name}
              value={nurseries.find((n) => n.id === form.nursery_id) || null}
              onChange={(e, v) => {
                handleChange("nursery_id", v?.id || null);
                resetTypeAndScheme();
              }}
              isOptionEqualToValue={(o, v) => o.id === v?.id}
              size="small"
              disabled={!form.financial_year || !form.department_id}
              renderInput={(p) => (
                <TextField
                  {...p}
                  label="Nursery"
                  error={!!errors.nursery_id}
                  helperText={
                    errors.nursery_id ||
                    (!form.department_id
                      ? "Select dept first"
                      : `${availableNurseries.length} available`)
                  }
                  sx={greenInputSx}
                />
              )}
            />
          </Grid>

          <Grid item xs={12} width="250px">
            <Autocomplete
              options={availableTypes}
              getOptionLabel={(o) => o}
              value={form.scheme_type || null}
              onChange={(e, v) => {
                handleChange("scheme_type", v || null);
                handleChange("scheme_id", null);
              }}
              isOptionEqualToValue={(o, v) => o === v}
              size="small"
              disabled={!form.nursery_id || !form.financial_year}
              renderInput={(p) => (
                <TextField
                  {...p}
                  label="Target Type"
                  error={!!errors.scheme_type}
                  helperText={
                    errors.scheme_type ||
                    (!form.nursery_id ? "Select nursery first" : "")
                  }
                  sx={greenInputSx}
                />
              )}
            />
          </Grid>

          {form.scheme_type === "Scheme" && (
            <Grid item xs={12} width="250px">
              <Autocomplete
                options={availableSchemes}
                getOptionLabel={(o) => o.name}
                value={
                  schemes.find(
                    (s) => Number(s.id) === Number(form.scheme_id),
                  ) || null
                }
                onChange={(e, v) => handleChange("scheme_id", v?.id || null)}
                isOptionEqualToValue={(o, v) => Number(o.id) === Number(v?.id)}
                size="small"
                renderInput={(p) => (
                  <TextField
                    {...p}
                    label="Scheme"
                    error={!!errors.scheme_id}
                    helperText={
                      errors.scheme_id || `${availableSchemes.length} remaining`
                    }
                    sx={greenInputSx}
                  />
                )}
              />
            </Grid>
          )}

          <Grid item xs={12} width="250px">
            <TextField
              fullWidth
              size="small"
              type="number"
              label="Target Quantity"
              placeholder="Enter quantity..."
              value={form.target_quantity}
              onChange={(e) => {
                let val = e.target.value;
                val = val.replace(/[eE+-]/g, "");
                if (val === "-") val = "";
                handleChange("target_quantity", val);
                const maxLimit = getMaxAllowed();
                if (maxLimit !== null && val !== "" && Number(val) > maxLimit) {
                  setErrors((prev) => ({
                    ...prev,
                    target_quantity:
                      maxLimit < 0
                        ? `Block limit exceeded by ${Math.abs(maxLimit).toLocaleString()}. Delete nursery targets first.`
                        : `Max limit is ${maxLimit.toLocaleString()}`,
                  }));
                } else if (
                  errors.target_quantity &&
                  (val === "" || Number(val) <= (maxLimit || 0))
                )
                  setErrors((prev) => {
                    const n = { ...prev };
                    delete n.target_quantity;
                    return n;
                  });
              }}
              inputProps={{ min: 0 }}
              error={!!errors.target_quantity}
              helperText={
                errors.target_quantity ||
                (getMaxAllowed() !== null
                  ? getMaxAllowed() < 0
                    ? `Limit exceeded by ${Math.abs(getMaxAllowed()).toLocaleString()}`
                    : `Max Limit: ${getMaxAllowed().toLocaleString()}`
                  : "Select scheme/type to see limit")
              }
              sx={greenInputSx}
            />
          </Grid>
        </Grid>
      </DynamicOffcanvas>
    </Box>
  );
};

export default NurseryTarget;

